import React, { useState } from 'react';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Button } from '../design/primitives';
import { apiClient } from '../api/apiConfig';
import { Business } from '../types/business';

interface BusinessValuationProps {
  business: Business;
  onUpdate?: () => void;
}

const formatCurrency = (n?: number) =>
  n != null ? `$${n.toLocaleString()}` : '$0';

export function BusinessValuation({ business, onUpdate }: BusinessValuationProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    revenue: String(business.revenue ?? 0),
    net_income: String(business.net_income ?? 0),
    owner_salary: String(business.owner_salary ?? 0),
    recurring_revenue: business.recurring_revenue ?? false,
    liabilities: String(business.liabilities ?? 0),
    broker_fee_pct: String((business.broker_fee_pct ?? 0.1) * 100),
    growth_stage: business.growth_stage ?? 'startup',
    multiple: business.multiple != null ? String(business.multiple) : '',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.patch(`/api/businesses/${business.id}/`, {
        revenue: parseFloat(form.revenue) || 0,
        net_income: parseFloat(form.net_income) || 0,
        owner_salary: parseFloat(form.owner_salary) || 0,
        recurring_revenue: form.recurring_revenue,
        liabilities: parseFloat(form.liabilities) || 0,
        broker_fee_pct: (parseFloat(form.broker_fee_pct) || 10) / 100,
        growth_stage: form.growth_stage,
        multiple: form.multiple ? parseFloat(form.multiple) : null,
      });
      setEditing(false);
      onUpdate?.();
    } catch (err) {
      console.error('Failed to update business financials', err);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: `${SPACING[4]} ${SPACING[8]}`,
    fontSize: TYPOGRAPHY.body.sizes.default,
    fontFamily: TYPOGRAPHY.body.fontFamily,
    border: `1px solid ${COLORS.border.default}`,
    borderRadius: RADIUS,
    backgroundColor: COLORS.bg.base,
    color: COLORS.text.primary,
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.body.sizes.small,
    color: COLORS.text.secondary,
    marginBottom: SPACING[4],
    display: 'block',
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: TYPOGRAPHY.body.sizes.default,
    color: COLORS.text.primary,
    padding: `${SPACING[4]} 0`,
  };

  return (
    <div style={{
      padding: SPACING[16],
      backgroundColor: COLORS.bg.panel,
      border: `1px solid ${COLORS.border.default}`,
      borderRadius: RADIUS,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACING[12] }}>
        <h2 style={{
          fontSize: TYPOGRAPHY.headings.sizes.h2,
          fontFamily: TYPOGRAPHY.headings.fontFamily,
          fontWeight: TYPOGRAPHY.weight.bold,
          color: COLORS.text.primary,
          margin: 0,
        }}>
          Valuation
        </h2>
        <Button variant="ghost" onClick={() => setEditing(!editing)} style={{ padding: `${SPACING[4]} ${SPACING[8]}` }}>
          {editing ? 'Cancel' : 'Edit'}
        </Button>
      </div>

      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING[12] }}>
            <div>
              <label style={labelStyle}>Annual Revenue ($)</label>
              <input style={inputStyle} type="number" value={form.revenue}
                onChange={e => setForm(f => ({ ...f, revenue: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Net Income ($)</label>
              <input style={inputStyle} type="number" value={form.net_income}
                onChange={e => setForm(f => ({ ...f, net_income: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Owner Salary ($)</label>
              <input style={inputStyle} type="number" value={form.owner_salary}
                onChange={e => setForm(f => ({ ...f, owner_salary: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Liabilities ($)</label>
              <input style={inputStyle} type="number" value={form.liabilities}
                onChange={e => setForm(f => ({ ...f, liabilities: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Growth Stage</label>
              <select style={inputStyle} value={form.growth_stage}
                onChange={e => setForm(f => ({ ...f, growth_stage: e.target.value }))}>
                <option value="startup">Startup</option>
                <option value="scaling">Scaling</option>
                <option value="mature">Mature</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Custom Multiple (optional)</label>
              <input style={inputStyle} type="number" step="0.1" placeholder="Auto"
                value={form.multiple}
                onChange={e => setForm(f => ({ ...f, multiple: e.target.value }))} />
            </div>
            <div>
              <label style={labelStyle}>Broker Fee (%)</label>
              <input style={inputStyle} type="number" step="0.1" value={form.broker_fee_pct}
                onChange={e => setForm(f => ({ ...f, broker_fee_pct: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8], paddingTop: SPACING[16] }}>
              <input type="checkbox" checked={form.recurring_revenue}
                onChange={e => setForm(f => ({ ...f, recurring_revenue: e.target.checked }))} />
              <label style={{ fontSize: TYPOGRAPHY.body.sizes.default, color: COLORS.text.primary }}>
                Recurring Revenue
              </label>
            </div>
          </div>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Financials'}
          </Button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[4] }}>
          <div style={rowStyle}>
            <span style={{ color: COLORS.text.secondary }}>SDE (Income + Salary)</span>
            <span style={{ fontWeight: '600' }}>{formatCurrency(business.sde)}</span>
          </div>
          <div style={rowStyle}>
            <span style={{ color: COLORS.text.secondary }}>Multiple</span>
            <span style={{ fontWeight: '600' }}>{business.valuation_multiple}x</span>
          </div>
          <div style={rowStyle}>
            <span style={{ color: COLORS.text.secondary }}>Gross Valuation</span>
            <span style={{ fontWeight: '600' }}>{formatCurrency(business.gross_valuation)}</span>
          </div>
          <div style={rowStyle}>
            <span style={{ color: COLORS.text.secondary }}>Sale Cost</span>
            <span style={{ fontWeight: '600', color: COLORS.status.error }}>-{formatCurrency(business.sale_cost)}</span>
          </div>
          {(business.liabilities ?? 0) > 0 && (
            <div style={rowStyle}>
              <span style={{ color: COLORS.text.secondary }}>Liabilities</span>
              <span style={{ fontWeight: '600', color: COLORS.status.error }}>-{formatCurrency(business.liabilities)}</span>
            </div>
          )}
          <div style={{ height: 1, backgroundColor: COLORS.border.default, margin: `${SPACING[4]} 0` }} />
          <div style={rowStyle}>
            <span style={{ fontWeight: '600' }}>Net Cash if Sold</span>
            <span style={{
              fontWeight: '700',
              color: (business.net_cash_if_sold ?? 0) > 0 ? COLORS.status.success : COLORS.text.primary,
            }}>
              {formatCurrency(business.net_cash_if_sold)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
